import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { type NutritionPlanId, newId, type ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssue, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * Where a nutrition plan comes from (D-096, pending).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS RECORDS A PLAN SOURCE AND ITS TARGETS. IT IS NOT A FOOD LOG.
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  There is nowhere here to put a food, a meal, a recipe, a micronutrient or a
 *  consumed quantity, and adding one needs a new decision rather than a new
 *  field. `AdherenceCheck` carries the same prohibition and a test asserts it on
 *  both.
 *
 *  **Neither Project Sola nor an AI originates a calorie or protein number in
 *  this version.** A numeric target reaches this record only because the user
 *  read it off a declared external authority — a nutrition app they configured,
 *  or a dietitian or coach who set it — and typed it in. Project Sola records
 *  what that authority currently recommends and **never predicts its next
 *  adjustment**.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY EFFECTIVE PERIODS RATHER THAN A MUTABLE TARGET
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  An external authority adjusts its targets as the user's data changes. If the
 *  stored target were overwritten each time, a weekly review from six weeks ago
 *  would be read against a number that did not exist then — the same retroactive
 *  rewriting D-046 forbids for a finalized opinion. So a change writes a **new
 *  record** and closes the previous one with an `effectiveTo`, and the history is
 *  retained.
 */

export const NUTRITION_SOURCES = [
  'externalNutritionApp',
  'dietitianOrCoach',
  'manualTarget',
  'none',
] as const

export type NutritionSource = (typeof NUTRITION_SOURCES)[number]

/** Display labels. Presentation only — never persisted, never compared. */
export const NUTRITION_SOURCE_LABELS: Readonly<Record<NutritionSource, string>> = {
  externalNutritionApp: 'An external nutrition app',
  dietitianOrCoach: 'A dietitian or coach',
  manualTarget: 'A target I set myself',
  none: 'No nutrition target',
}

export function isNutritionSource(value: string): value is NutritionSource {
  return (NUTRITION_SOURCES as readonly string[]).includes(value)
}

/**
 * Whether the plan carries numbers at all.
 *
 * A coach may hand over a meal plan with no calorie figure in it, which is a
 * real plan the user can follow and report on. `nonNumeric` is what makes the
 * daily question "did you follow your plan?" rather than "were you within your
 * calorie target?", and it is why a missing calorie target here is not a gap.
 */
export const NUTRITION_PLAN_KINDS = ['numeric', 'nonNumeric'] as const

export type NutritionPlanKind = (typeof NUTRITION_PLAN_KINDS)[number]

export function isNutritionPlanKind(value: string): value is NutritionPlanKind {
  return (NUTRITION_PLAN_KINDS as readonly string[]).includes(value)
}

/** Whether a recorded calorie figure is a daily target or a weekly average. */
export const CALORIE_TARGET_BASES = ['perDay', 'weeklyAverage'] as const

export type CalorieTargetBasis = (typeof CALORIE_TARGET_BASES)[number]

export function isCalorieTargetBasis(value: string): value is CalorieTargetBasis {
  return (CALORIE_TARGET_BASES as readonly string[]).includes(value)
}

/**
 * Who the rules engine treats as the numeric authority (D-096).
 *
 * Three values and not four, because the engine's question is narrower than the
 * record's: it does not care *which* outside system sets the numbers, only
 * whether one does. `'sola'` means the target lives here because the user set it
 * themselves; `'external'` covers both an app they configured and a person who
 * coaches them, and the wording under it must therefore read correctly for both.
 *
 * **The provider label never reaches this vocabulary, and that is deliberate.**
 * A rule's text is stored on the opinion and serialized into every export, where
 * the label itself gates on `includeNotes`. Interpolating "MacroFactor" or
 * "Anna" into an action sentence would carry it past that gate inside a string
 * nothing can redact — the export-gating rule in its newest costume. The screens
 * name the provider; the rules say "the system that sets your targets".
 */
export const NUTRITION_AUTHORITIES = ['sola', 'external', 'none'] as const

export type NutritionAuthority = (typeof NUTRITION_AUTHORITIES)[number]

/** Which authority a source implies. Total over `NutritionSource`, no default. */
export function authorityOf(source: NutritionSource): NutritionAuthority {
  switch (source) {
    case 'externalNutritionApp':
    case 'dietitianOrCoach':
      return 'external'
    case 'manualTarget':
      return 'sola'
    case 'none':
      return 'none'
  }
}

export interface NutritionPlanRecord extends RecordEnvelope {
  readonly id: NutritionPlanId
  readonly projectId: ProjectId
  readonly source: NutritionSource
  /**
   * Whether the user asked for the daily three taps, where they are optional.
   *
   * Only consulted for `externalNutritionApp`, where D-096 makes the daily
   * questions **off by default**: someone already logging every meal in another
   * app has answered these questions there, and asking again is the duplicate
   * logging that decision rules out. Every other source that has a plan at all
   * asks daily, so the field is absent on those rather than set to `true` — an
   * absent flag is "not applicable", not "declined".
   */
  readonly dailyChecksEnabled?: boolean
  /**
   * What the user calls the authority — "MacroFactor", "Anna".
   *
   * Free text the user typed, and therefore gated on `includeNotes` in every
   * export rather than treated as neutral metadata: naming a third-party service
   * or a person discloses something about the user, not about the data. This is
   * the same rule as the project name.
   */
  readonly providerLabel?: string
  readonly planKind: NutritionPlanKind
  readonly calorieTarget?: number
  readonly calorieTargetBasis?: CalorieTargetBasis
  readonly proteinTargetG?: number
  /**
   * The weekly rate the external authority is configured for.
   *
   * This is the field that makes a conflict detectable: Project Sola can compare
   * its own phase target range against what the user says the other system is
   * set to, and **show the disagreement rather than resolving it.** Signed
   * percent of body weight per week, matching `PhaseRecord`, so the two are
   * directly comparable without a conversion nobody would remember to apply.
   */
  readonly configuredGoalRatePercent?: number
  readonly effectiveFrom: CalendarDate
  /** Set when a later record supersedes this one. Absent while current. */
  readonly effectiveTo?: CalendarDate
  readonly note?: string
}

export interface NutritionPlanInput {
  readonly projectId: string
  readonly source: string
  readonly planKind: string
  readonly effectiveFrom: string
  readonly providerLabel?: string
  readonly calorieTarget?: number
  readonly calorieTargetBasis?: string
  readonly proteinTargetG?: number
  readonly configuredGoalRatePercent?: number
  readonly note?: string
  readonly dailyChecksEnabled?: boolean
}

const MAX_LABEL_LENGTH = 60
const MAX_NOTE_LENGTH = 500

/**
 * Plausibility bounds, and what they are not.
 *
 * These are **not coaching guidance and not a recommendation** — they are the
 * range outside which a typed number is almost certainly a slip, in the same
 * spirit as the measurement spread warning. They exist so a mistyped 21000 is
 * caught, never to tell the user their target is wrong: the authority that set
 * it is not this app. No rule reads them and they belong in no threshold table.
 */
const MIN_PLAUSIBLE_CALORIES = 600
const MAX_PLAUSIBLE_CALORIES = 8000
const MAX_PLAUSIBLE_PROTEIN_G = 500

/** Multiplier for a weekly-average figure. Not a calendar concept — a phase week
    is D-034's business — just the number of days a weekly average covers. */
const DAYS_IN_WEEK = 7

export function validateNutritionPlan(input: NutritionPlanInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (input.projectId.trim().length === 0) {
    issues.push(issue('projectId', 'A nutrition plan must belong to a project.'))
  }
  if (!isNutritionSource(input.source)) {
    issues.push(issue('source', `Source must be one of: ${NUTRITION_SOURCES.join(', ')}.`))
  }
  if (!isNutritionPlanKind(input.planKind)) {
    issues.push(issue('planKind', `Plan kind must be one of: ${NUTRITION_PLAN_KINDS.join(', ')}.`))
  }

  if (toCalendarDate(input.effectiveFrom) === undefined) {
    issues.push(issue('effectiveFrom', 'Effective date must be a real date in YYYY-MM-DD form.'))
  }

  if (input.providerLabel !== undefined && input.providerLabel.length > MAX_LABEL_LENGTH) {
    issues.push(issue('providerLabel', `A name may be at most ${MAX_LABEL_LENGTH} characters.`))
  }

  issues.push(...targetIssues(input))

  if (
    input.configuredGoalRatePercent !== undefined &&
    !Number.isFinite(input.configuredGoalRatePercent)
  ) {
    issues.push(issue('configuredGoalRatePercent', 'A weekly rate must be a real number.'))
  }

  if (input.note !== undefined && input.note.length > MAX_NOTE_LENGTH) {
    issues.push(issue('note', `A note may be at most ${MAX_NOTE_LENGTH} characters.`))
  }

  return issues
}

/**
 * Target coherence.
 *
 * A `none` source may not carry targets — that combination would put a number on
 * a plan the user said they do not have. A `numeric` plan must carry at least
 * one of the two, or it is `nonNumeric` mislabelled. A calorie figure must say
 * whether it is a day or a weekly average, because comparing the two silently is
 * a seven-fold error.
 */
function targetIssues(input: NutritionPlanInput): ValidationIssues {
  const issues: ValidationIssue[] = []
  const hasCalories = input.calorieTarget !== undefined
  const hasProtein = input.proteinTargetG !== undefined

  if (input.source === 'none' && (hasCalories || hasProtein)) {
    issues.push(
      issue('source', 'Remove the targets, or choose where the plan comes from — not both.'),
    )
  }

  if (input.planKind === 'numeric' && !hasCalories && !hasProtein) {
    issues.push(
      issue('planKind', 'A numeric plan needs a calorie target, a protein target, or both.'),
    )
  }

  if (hasCalories) issues.push(...calorieIssues(input))
  if (hasProtein) issues.push(...proteinIssues(input.proteinTargetG as number))

  return issues
}

/**
 * A calorie figure and its basis.
 *
 * The basis is required whenever a figure is present, because a daily target and
 * a weekly average differ by a factor of seven and comparing them silently is a
 * wrong answer rather than a rounding error.
 */
function calorieIssues(input: NutritionPlanInput): ValidationIssues {
  const issues: ValidationIssue[] = []
  const calories = input.calorieTarget as number

  /* The bounds scale with the basis. They were written for a daily figure, and
     checking a weekly average against them rejected a perfectly ordinary 17,150 —
     the same class of error as comparing the two silently, which is why the basis
     is required at all. */
  const perWeek = input.calorieTargetBasis === 'weeklyAverage'
  const multiplier = perWeek ? DAYS_IN_WEEK : 1
  const minimum = MIN_PLAUSIBLE_CALORIES * multiplier
  const maximum = MAX_PLAUSIBLE_CALORIES * multiplier

  if (!Number.isFinite(calories) || calories < minimum || calories > maximum) {
    issues.push(
      issue(
        'calorieTarget',
        `Check that figure — Project Sola expects between ${minimum} and ${maximum} ${
          perWeek ? 'for a weekly average' : 'per day'
        }.`,
      ),
    )
  }

  if (input.calorieTargetBasis === undefined) {
    issues.push(issue('calorieTargetBasis', 'Say whether that is per day or a weekly average.'))
  } else if (!isCalorieTargetBasis(input.calorieTargetBasis)) {
    issues.push(
      issue('calorieTargetBasis', `Basis must be one of: ${CALORIE_TARGET_BASES.join(', ')}.`),
    )
  }

  return issues
}

function proteinIssues(protein: number): ValidationIssues {
  if (Number.isFinite(protein) && protein > 0 && protein <= MAX_PLAUSIBLE_PROTEIN_G) return []
  return [
    issue(
      'proteinTargetG',
      `Check that figure — Project Sola expects up to ${MAX_PLAUSIBLE_PROTEIN_G} g.`,
    ),
  ]
}

/** Build a validated nutrition plan without touching storage. */
export function buildNutritionPlanRecord(
  input: NutritionPlanInput,
  now: IsoTimestamp,
): Result<NutritionPlanRecord, ValidationIssues> {
  const issues = validateNutritionPlan(input)
  if (issues.length > 0) return err(issues)

  const effectiveFrom = toCalendarDate(input.effectiveFrom)
  if (effectiveFrom === undefined) {
    return err([issue('nutritionPlan', 'Nutrition plan dates failed construction.')])
  }

  return ok({
    id: newId<'NutritionPlanId'>(),
    projectId: input.projectId as ProjectId,
    source: input.source as NutritionSource,
    ...(input.providerLabel === undefined ? {} : { providerLabel: input.providerLabel.trim() }),
    planKind: input.planKind as NutritionPlanKind,
    ...(input.calorieTarget === undefined ? {} : { calorieTarget: input.calorieTarget }),
    ...(input.calorieTargetBasis === undefined
      ? {}
      : { calorieTargetBasis: input.calorieTargetBasis as CalorieTargetBasis }),
    ...(input.proteinTargetG === undefined ? {} : { proteinTargetG: input.proteinTargetG }),
    ...(input.configuredGoalRatePercent === undefined
      ? {}
      : { configuredGoalRatePercent: input.configuredGoalRatePercent }),
    effectiveFrom,
    ...(input.note === undefined ? {} : { note: input.note }),
    ...(input.dailyChecksEnabled === undefined
      ? {}
      : { dailyChecksEnabled: input.dailyChecksEnabled }),
    createdAt: now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * Close a record because a newer one takes over.
 *
 * `effectiveTo` is the day *before* the successor begins, because a plan period
 * is inclusive at both ends — the same rule as a phase span, and the same defect
 * is available here. Two plans sharing a day would let one day's check be judged
 * against two different targets.
 *
 * Refuses rather than silently adjusting when the successor starts on or before
 * this record's own start: a zero-length or negative period is a caller bug, and
 * quietly producing one would hide it.
 */
export function supersedeNutritionPlan(
  record: NutritionPlanRecord,
  successorEffectiveFrom: CalendarDate,
  now: IsoTimestamp,
): Result<NutritionPlanRecord, ValidationIssues> {
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  if (unwrapDate(successorEffectiveFrom) <= unwrapDate(record.effectiveFrom)) {
    return err([
      issue(
        'effectiveFrom',
        'New targets must start after the ones they replace, so no day is covered by two plans.',
      ),
    ])
  }

  const effectiveTo = dayBefore(successorEffectiveFrom)
  if (effectiveTo === undefined) {
    return err([issue('effectiveFrom', 'That date could not be read.')])
  }

  return ok({ ...record, effectiveTo, updatedAt: now, schemaVersion: DB_SCHEMA_VERSION })
}

/**
 * The calendar day before this one.
 *
 * Built from the date parts rather than by subtracting milliseconds: a UTC
 * arithmetic shortcut lands on the wrong day across a daylight-saving boundary,
 * and `CalendarDate` is a local calendar date by definition (D-034).
 */
function dayBefore(date: CalendarDate): CalendarDate | undefined {
  const [year, month, day] = unwrapDate(date).split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) return undefined

  const shifted = new Date(year, month - 1, day - 1)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return toCalendarDate(
    `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`,
  )
}

/**
 * Whether this plan puts the three taps on Today (D-096's daily column).
 *
 * A plan with no source asks nothing — **no empty task and no prompt**, because
 * a task that cannot be completed is worse than no task. An external app asks
 * only where the user opted in: they are already logging in that app, and asking
 * again is the duplicate logging D-096 rules out. A coach's plan and a target the
 * user set themselves both ask, because nothing else is collecting the answer.
 */
export function requiresDailyCheck(record: NutritionPlanRecord): boolean {
  if (record.source === 'none') return false
  if (record.source === 'externalNutritionApp') return record.dailyChecksEnabled === true
  return true
}

/**
 * Which daily question this plan asks.
 *
 * A `nonNumeric` plan has no figure to be inside, so asking "were you within
 * your calorie target?" would be asking about a number nobody set. The
 * `followedPlan` answer is the whole nutrition block for such a day, and
 * `validateDailyCheckPatch` refuses a day carrying both shapes.
 */
export function dailyQuestionKindFor(record: NutritionPlanRecord): 'targets' | 'followedPlan' {
  return record.planKind === 'nonNumeric' ? 'followedPlan' : 'targets'
}

/** Whether a protein question is meaningful for this plan. */
export function hasProteinTarget(record: NutritionPlanRecord): boolean {
  return record.proteinTargetG !== undefined
}
